/* ------------------------------------------------------------------------------------------------------------------
   KLEEP "FIND YOUR SIZE" — global trigger
   ------------------------------------------------------------------------------------------------------------------

   The Kleep app binds a click handler to the "Find your size" buttons it finds when it loads. Every button the
   theme renders outside that moment — the one in the mobile sizes sheet, the ones inside quick-buy modals, whose
   markup is injected on demand — is left dead, and its markup calls a global kleep_cta(). The app used to define
   that global and no longer does. This file defines it.

   Two things it has to sort out before opening the drawer:

   1. The widget is a single iframe configured for one product. Opening it straight from a card in Related products
      would recommend a size for the wrong garment, so it gets repointed at the product the clicked button belongs
      to first. The repoint mirrors what the app does when its own CTA is replaced on SPA navigation — same
      product_id search param, same detach/reattach so no history entry is pushed.

   2. Outside product templates the app embed sets enabledOnPage = false, and its loader then stops before building
      anything: no iframe, no drawer, no open handler. Collection and search pages are exactly that case, and their
      quick-buy sheets do show a "Find your size". So on those pages the widget is built here, on the first tap, and
      the app's own loader is run over it — see bootstrap() below.

   Either way the panel stays down until the iframe has that product on screen — the app opens it synchronously
   from its own handler on these same buttons, which would otherwise flash the previous card's recommendation.
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  const IDS = { wrapper: 'kleep-size-wrapper', iframe: 'kleep-size-iframe', primaryCta: 'kleep-size-button' };

  const SELECTORS = { wrapper: `#${IDS.wrapper}`, iframe: `#${IDS.iframe}` };

  const CLASSES = { wrapper: 'kleep-wrapper', iframe: 'kleep-iframe', proxyCta: 'kleep-cta-proxy' };

  const PARAMS = {
    productId: 'product_id',
    variantId: 'variantId',
    domain: 'domain',
    customerId: 'customer_id',
    lang: 'lang',
    countryCode: 'countryCode'
  };

  // Hook for the rule in custom.css that holds the panel down while the iframe loads
  const RELOADING_ATTRIBUTE = 'data-kleep-reloading';

  // The drawer is a page hosted by Kleep; this is the app embed's own "kleep_url" setting.
  const DRAWER_URL = 'https://drawer.kleep.ai/';

  const IFRAME_TITLE = 'Kleep.ai';

  // Past this the panel opens regardless: a slow iframe should degrade to a spinner inside the
  // drawer, never to a tap that looks like it did nothing.
  const IFRAME_LOAD_TIMEOUT_MS = 2000;

  const sdk = () => window.KleepSDK;

  // Queried on every call rather than cached: these nodes belong to the app, which creates,
  // reattaches and tears them down on its own schedule.
  const panel = () => document.querySelector(SELECTORS.wrapper);
  const frame = () => document.querySelector(SELECTORS.iframe);

  // The app publishes its open handler at the end of a successful load, so this doubles as
  // "the widget is built and wired".
  const isReady = () => typeof sdk()?.__onMainCta === 'function';

  const openPanel = () => sdk()?.__onMainCta?.();

  const closePanel = () => {
    const wrapper = panel();

    if (wrapper) {
      wrapper.style.display = 'none';
    }

    if (sdk()?.__a11y) {
      sdk().__a11y.open = false;
    }
  };

  // Cross-origin once the drawer document has committed, which is what turns this null. The app
  // uses the same test to know its iframe is up. Only meaningful for an iframe whose src has not
  // just been changed: a fresh cross-origin src reads null from the start, loaded or not.
  const isLoaded = (iframe) => iframe.contentDocument === null;

  // Mirrors the app's own helper: swapping src while detached keeps it out of session history.
  const setIframeSrc = (iframe, url) => {
    const parent = iframe.parentElement;

    if (!parent) {
      return;
    }

    const nextSibling = iframe.nextSibling;
    parent.removeChild(iframe);
    iframe.dataset.src = url;
    iframe.src = url;
    parent.insertBefore(iframe, nextSibling);
  };

  // The product whose iframe we are waiting on. Also the guard that stops a slower earlier
  // tap from opening the panel after the customer has moved on to another card.
  let pendingProductId = null;

  // Holds the panel down — the app opens it synchronously from its own handler on these same
  // buttons — until the iframe has finished loading the product that was asked for.
  const openAfterLoad = (iframe, productId) => {
    panel()?.setAttribute(RELOADING_ATTRIBUTE, '');
    pendingProductId = productId;

    const settle = () => {
      if (pendingProductId !== productId) {
        return;
      }

      pendingProductId = null;
      panel()?.removeAttribute(RELOADING_ATTRIBUTE);
      openPanel();
    };

    iframe.addEventListener('load', settle, { once: true });
    setTimeout(settle, IFRAME_LOAD_TIMEOUT_MS);
  };

  // Straight after a bootstrap, where the app's own product check has already given the iframe
  // a head start and its load event may be gone by now.
  const openWhenLoaded = (iframe, productId) => {
    if (isLoaded(iframe)) {
      openPanel();
      return;
    }

    openAfterLoad(iframe, productId);
  };

  // Returns true when the iframe had to be reloaded for a different product.
  const pointAtProduct = (productId) => {
    const iframe = frame();

    if (!iframe || !productId) {
      return false;
    }

    const url = new URL(iframe.src, window.location.href);

    if (url.searchParams.get(PARAMS.productId) === productId) {
      return false;
    }

    url.searchParams.set(PARAMS.productId, productId);
    // Any variant already on the url belongs to the product we are leaving behind
    url.searchParams.delete(PARAMS.variantId);

    closePanel();
    setIframeSrc(iframe, url.toString());

    return true;
  };

  // Same shape as the src the app embed renders on a product page.
  const buildDrawerUrl = (productId) => {
    const url = new URL(DRAWER_URL);

    url.searchParams.set(PARAMS.domain, sdk()?.shopHost || window.location.host);
    url.searchParams.set(PARAMS.productId, productId);
    url.searchParams.set(PARAMS.customerId, window.ShopifyAnalytics?.meta?.page?.customerId || '');
    url.searchParams.set(PARAMS.lang, window.Shopify?.locale || document.documentElement.lang);
    url.searchParams.set(PARAMS.countryCode, window.Shopify?.country || '');

    return url.toString();
  };

  const createWidget = (productId) => {
    const wrapper = document.createElement('div');
    wrapper.id = IDS.wrapper;
    wrapper.className = CLASSES.wrapper;

    const iframe = document.createElement('iframe');
    iframe.id = IDS.iframe;
    iframe.className = CLASSES.iframe;
    iframe.title = IFRAME_TITLE;
    // The app reads this to know which origin its postMessages must come from
    iframe.dataset.iframeUrl = DRAWER_URL;
    iframe.src = buildDrawerUrl(productId);
    iframe.dataset.src = iframe.src;
    wrapper.append(iframe);

    // The app's loader gives up unless its own primary CTA is on the page, and reads the product
    // to check from that button. Collection pages have none of their own — "Find your size" there
    // lives on a card — so stand one in for it, out of sight.
    const proxyCta = document.createElement('button');
    proxyCta.type = 'button';
    proxyCta.id = IDS.primaryCta;
    proxyCta.className = CLASSES.proxyCta;
    proxyCta.tabIndex = -1;
    proxyCta.setAttribute('aria-hidden', 'true');
    proxyCta.dataset.productId = productId;

    document.body.append(wrapper);
    // Prepended, not appended: an open quick-buy modal carries a primary CTA of its own, and the
    // loader takes the first one in the document. First child of the body wins that outright,
    // which keeps the product being checked the one that was actually tapped.
    document.body.prepend(proxyCta);
  };

  const removeWidget = () => {
    panel()?.remove();
    document.querySelector(`.${CLASSES.proxyCta}`)?.remove();
  };

  // Builds the two nodes the app's loader needs, flips the flag it bailed on and runs it. The
  // app then wires its own drawer, message handler and focus trap exactly as on a product page,
  // which is the point: none of that behaviour is reimplemented here.
  const bootstrap = async (productId) => {
    // A global from the app's own script (kleep-app.js), which is a classic script like this
    // one, so its top-level bindings are in reach.
    if (!productId || typeof kleepLoadContent !== 'function') {
      return false;
    }

    createWidget(productId);
    sdk().enabledOnPage = true;

    try {
      await kleepLoadContent();
    } catch {
      // Nothing to add: the readiness check below is what decides either way
    }

    // The loader tears its own nodes down when Kleep has no sizing for this product. Clear out
    // whatever is left so the next tap can try again with its own product instead of finding a
    // half-built widget and assuming it works.
    if (!isReady()) {
      removeWidget();
      return false;
    }

    return true;
  };

  // `trigger` is the button itself, passed as `this` from its onclick. Without it the widget
  // just opens for whatever product it is already showing, which is correct on a product page.
  window.kleep_cta = async (trigger) => {
    if (!sdk()) {
      return;
    }

    const productId = trigger?.dataset?.productId;
    const wasReady = isReady();

    if (!wasReady && !(await bootstrap(productId))) {
      return;
    }

    const iframe = frame();

    if (!iframe) {
      return;
    }

    if (!wasReady) {
      openWhenLoaded(iframe, productId);
      return;
    }

    if (pointAtProduct(productId)) {
      openAfterLoad(iframe, productId);
      return;
    }

    openPanel();
  };
})();
